import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, shape, string, func, object } from 'prop-types';
import { TextStyled } from '@nike/nike-design-system-components';
import Link from '../Link';
import { StyledDiv, dropDownSubListTitle, dropDownListTitle, dropDownSubListItem } from './styles';
import AnalyticsEventTypes from './../../utils/analyticsEvent';
import { LOG_OUT_BUTTON_PATH } from '../../constants';
import { isHeader, generateMenuSections } from '../../utils/menuUtils';

const LinkColumn = props => {
  const { content, columnChildren, textAppearance = 'body3Strong', containerClassName } = props;
  const ref = useRef(null);
  let childContent = content?.content;
  if (columnChildren) {
    childContent = columnChildren(content);
  }
  const analyticsEventType = content.analyticsEventName || AnalyticsEventTypes.CLICK_NAV_MEMBERSHIP;
  const handleLogOut = () => {
    window.webShellClient.identity.signOut().finally({});
  };
  const titleAnalyticsPayload = {
    props: { path: content?.path?.join(':') },
    type: analyticsEventType,
  };

  // setting these values to follow V2 and product analytics standard
  if (analyticsEventType === AnalyticsEventTypes.CLICK_NAV_ACCOUNT) {
    titleAnalyticsPayload.props.path = 'myAccount:'.concat(content?.classification);
    titleAnalyticsPayload.props.hashKey = content?.hashKey;
  }
  if (analyticsEventType === AnalyticsEventTypes.CLICK_HELP_MENU) {
    titleAnalyticsPayload.props.hashKey = content?.hashKey;
  }
  if (analyticsEventType === AnalyticsEventTypes.CLICK_NAV_FOOTER) {
    titleAnalyticsPayload.props.hashKey = content?.hashKey;
  }
  /**
   * When we close the mobile menu we want focus to be returned to this button
   * unfortunately during the rendering that focus is being lost and set on the body.
   * This state remembers that the focus was set here and waits until the next render
   * to set focus on the button
   */
  const [shouldRerender, setShouldRerender] = useState(null);
  const shouldBeFocused = useRef(false);
  useEffect(() => {
    if (shouldBeFocused.current) {
      ref.current.focus();
    }
  }, [shouldRerender]);

  const generateMenuSubSection = (childContent, index) => {
    const listName = childContent?.[0]?.label;
    return (
      <ul
        key={index}
        // conditionally render aria-label if listName exists
        {...(listName ? { 'aria-label': listName } : {})}
      >
        {childContent?.map((contentItem, key) => {
          const analyticsPayload = {
            props: { path: contentItem?.path?.join(':'), hashKey: contentItem?.hashKey },
            type: analyticsEventType,
          };

          if (analyticsEventType === AnalyticsEventTypes.CLICK_NAV_ACCOUNT)
            analyticsPayload.props.path = 'myAccount:'.concat(contentItem?.classification);

          return (
            <li
              className={isHeader(contentItem) ? dropDownSubListTitle : dropDownSubListItem}
              key={`${contentItem?.label}-${key}`}
            >
              <Link
                clickEventPayload={analyticsPayload}
                href={contentItem?.url}
                onClick={
                  contentItem?.path?.join(':') === LOG_OUT_BUTTON_PATH ? handleLogOut : undefined
                }
                onFocus={() => {
                  if (isHeader(contentItem) && index === 0) {
                    if (!shouldBeFocused.current) setShouldRerender(!shouldRerender);
                    shouldBeFocused.current = !shouldBeFocused.current;
                    return;
                  }
                }}
                ref={isHeader(contentItem) && index === 0 ? ref : null}
                role="menuitem"
              >
                <TextStyled
                  appearance={isHeader(contentItem) ? 'body2Strong' : textAppearance}
                  role="presentation"
                >
                  {contentItem?.label}
                </TextStyled>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  };

  const generateMenu = childContent => {
    return <>{generateMenuSections(childContent).map(generateMenuSubSection)}</>;
  };

  return (
    <StyledDiv className={containerClassName} role="menu">
      {content?.title && (
        <Link
          className={dropDownListTitle}
          clickEventPayload={titleAnalyticsPayload}
          href={content?.titleLink}
        >
          <TextStyled appearance={textAppearance} color="secondary">
            {content?.title}
          </TextStyled>
        </Link>
      )}
      {generateMenu(childContent)}
    </StyledDiv>
  );
};

export default LinkColumn;

LinkColumn.propTypes = {
  content: shape({
    content: arrayOf(object),
    title: string,
    titleLink: string,
  }),
  columnChildren: func,
  id: string,
  textAppearance: string,
  ListItemComponent: object,
  containerClassName: string,
};
